import React, { type ReactNode } from 'react';

export const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface TablePagingFooterProps {
  currentPage: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  /** Extra content rendered next to the "Showing x–y of z" text (e.g. active drilldown filter chip). */
  startExtra?: ReactNode;
  /** Set false when the table above already renders its own bottom border, to avoid a doubled-up border line. */
  bordered?: boolean;
}

const PaginationSkeleton: React.FC = () => (
  <div className="flex flex-shrink-0 items-center justify-between gap-x-3 gap-y-1 border-t border-gray-300 bg-gray-50 px-3 py-1 dark:border-white/30 dark:bg-[#3C5C73]">
    <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
    <div className="flex items-center gap-2">
      <div className="h-[2em] w-[2em] animate-pulse rounded-md bg-gray-200" />
      <div className="h-4 w-16 animate-pulse rounded bg-gray-200" />
      <div className="h-[2em] w-[2em] animate-pulse rounded-md bg-gray-200" />
    </div>
  </div>
);

export const TablePagingFooter: React.FC<TablePagingFooterProps> = ({
  currentPage,
  totalPages,
  total,
  pageSize,
  onPageChange,
  isLoading = false,
  pageSizeOptions,
  onPageSizeChange,
  startExtra,
  bordered = true,
}) => {
  if (isLoading) {
    return <PaginationSkeleton />;
  }

  const hasItems = total > 0;
  const startIndex = hasItems ? (currentPage - 1) * pageSize + 1 : 0;
  const endIndex = hasItems ? Math.min(currentPage * pageSize, total) : 0;

  const canGoPrev = hasItems && currentPage > 1;
  const canGoNext = hasItems && currentPage < totalPages;

  const buttonClass =
    'inline-grid size-[2em] shrink-0 place-items-center rounded-md border border-gray-300 bg-white shadow-sm transition-colors hover:bg-gray-100 disabled:opacity-50 dark:border-white dark:bg-transparent dark:shadow-none dark:hover:bg-gray-600';

  return (
    <div className={`flex flex-shrink-0 flex-row flex-wrap items-center justify-between gap-x-3 gap-y-1 ${bordered ? 'border-t border-gray-300 dark:border-white/30' : ''} bg-gray-50 px-3 py-1 text-xs font-normal text-primary dark:bg-[#3C5C73] dark:text-white sm:px-4`}>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <div className="min-w-0 whitespace-nowrap">
          Showing {startIndex}–{endIndex} of {total}
        </div>
        {startExtra}
      </div>
      <div className="flex items-center gap-2 sm:ml-auto">
        {pageSizeOptions && onPageSizeChange && (
          <label className="flex items-center gap-1.5">
            <span className="hidden whitespace-nowrap sm:inline">Rows per page</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => onPageSizeChange(Number(v))}
            >
              <SelectTrigger className="box-border inline-flex h-[2em] min-h-[2em] w-fit min-w-[2rem] shrink-0 justify-start gap-1 px-1.5 py-0 text-xs leading-normal shadow-sm dark:shadow-none dark:bg-transparent dark:border-white/30 dark:text-white [&_svg]:size-3 [&_svg]:shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end" className="min-w-[3rem]">
                {pageSizeOptions.map((n) => (
                  <SelectItem key={n} value={String(n)} className="py-1 text-xs">
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        )}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            className={buttonClass}
            onClick={() => canGoPrev && onPageChange(currentPage - 1)}
            disabled={!canGoPrev}
            aria-label="Previous page"
          >
            <ChevronLeft className="size-[1em]" />
          </button>
          <span className="px-1">
            {hasItems ? currentPage : 0} / {hasItems ? totalPages : 0}
          </span>
          <button
            type="button"
            className={buttonClass}
            onClick={() => canGoNext && onPageChange(currentPage + 1)}
            disabled={!canGoNext}
            aria-label="Next page"
          >
            <ChevronRight className="size-[1em]" />
          </button>
        </div>
      </div>
    </div>
  );
};
